import { useCallback, useEffect } from 'react';

/**
 * Input types that hold no typed text. Radios and checkboxes take focus when
 * their label is clicked, and react-bootstrap builds the tool toggles and the
 * annotation class picker out of exactly those, so treating every <input> as a
 * text field would swallow the shortcuts for the rest of the click's lifetime.
 * These controls answer to Space and Enter only, so a bare letter is safe.
 */
const NON_TEXT_INPUT_TYPES = [
	'button',
	'checkbox',
	'color',
	'file',
	'radio',
	'range',
	'reset',
	'submit',
];

/**
 * Whether a keystroke belongs to the element that has focus rather than to a
 * shortcut. Text fields, selects and contenteditable regions own their keys:
 * these shortcuts are single letters, so without this check typing a name into
 * any input silently fires them.
 *
 * @param {EventTarget|null} target event target to test
 * @returns {boolean} true when the user is typing into the target
 */
const isTypingTarget = target => {
	if (!target) return false;
	if (target.isContentEditable) return true;

	if (target.tagName === 'INPUT')
		return !NON_TEXT_INPUT_TYPES.includes(target.type?.toLowerCase());

	return ['TEXTAREA', 'SELECT'].includes(target.tagName);
};

/**
 * Custom hook that listens for a keydown event and runs a callback function if the key matches the target key.
 *
 * @param {*} targetKey key that must be pressed for the callback to run
 * @param {*} condition condition that must be met for the callback to run
 * @param {*} callback callback function to run if the key matches the target key
 */
export default function useKeyboardShortcut(targetKey, condition, callback) {
	const handleKeyDown = useCallback(
		event => {
			// Escape is exempt: it inserts nothing, and dismissing from a focused
			// field is what the user expects.
			if (event.key !== 'Escape' && isTypingTarget(event.target)) return;

			// These are bare single-key shortcuts, so a combination belongs to the
			// browser or the OS: Ctrl+A must select text, not pick the Area tool.
			if (event.ctrlKey || event.metaKey || event.altKey) return;

			if (
				event.key?.toString().toLowerCase() === targetKey.toString().toLowerCase() &&
				condition
			) {
				callback();
			}
		},
		[targetKey, condition, callback]
	);

	useEffect(() => {
		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [handleKeyDown]);
}
